"use client";

import Link from "next/link";
import Image from "next/image";
import { useCallback, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import {
  ChevronDown,
  ChevronRight,
  GraduationCap,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

import {
  ProgressBar,
  PANEL,
  RuleHeader,
  InitialsTile,
} from "@/components/home/HomePrimitives";
import { getHomeAdminData } from "@/server-actions/home/getHomeAdminData";
import {
  adminPersonHref,
  adminLearningPathHref,
} from "@/lib/admin-entity-links";
import CoachingActions from "@/components/workspaces/reports/CoachingActions";

const PERIOD_OPTIONS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

function PeriodSelect({ value, onChange, disabled }) {
  return (
    <div className="relative">
      <label htmlFor="team-period" className="sr-only">
        Time period
      </label>
      <select
        id="team-period"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
        className="appearance-none rounded-lg border border-gray-200 bg-white py-1.5 pl-3 pr-8 text-[12.5px] font-medium text-gray-700 transition-colors hover:border-gray-300 focus:border-brand-500 focus:outline-none disabled:opacity-60"
      >
        {PERIOD_OPTIONS.map((option) => (
          <option key={option.days} value={option.days}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
    </div>
  );
}

function StatLine({ stats }) {
  if (!stats?.length) return null;
  return (
    <dl
      className={`${PANEL} animate-rise mb-6 flex flex-wrap overflow-hidden`}
      style={{ "--rise-delay": "40ms" }}
    >
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="min-w-[130px] flex-1 border-l border-gray-100 px-5 py-4 first:border-l-0"
        >
          <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
            {stat.label}
          </dt>
          <dd className="mt-2 flex items-baseline gap-2">
            <span className="text-[26px] font-semibold leading-none tracking-tight tabular-nums text-gray-900">
              {stat.value}
            </span>
            {stat.delta && (
              <span
                className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                  stat.dir === "up"
                    ? "text-emerald-600"
                    : stat.dir === "down"
                      ? "text-rose-600"
                      : "text-gray-400"
                }`}
              >
                {stat.dir === "up" ? (
                  <TrendingUp className="h-3 w-3" />
                ) : stat.dir === "down" ? (
                  <TrendingDown className="h-3 w-3" />
                ) : null}
                {stat.delta}
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function AttentionRow({ member, workspaceId, timeframeDays }) {
  const [expanded, setExpanded] = useState(false);
  const tone = member.tag === "down" ? "rose" : "amber";
  const href = adminPersonHref(member.userId);
  const toggle = () => setExpanded((v) => !v);

  return (
    <div className="border-b border-gray-200/70 last:border-b-0">
      <div className="flex items-center gap-3 py-3.5">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-4 text-left transition-opacity hover:opacity-80"
        >
          <InitialsTile name={member.name} tone={tone} />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[15px] font-semibold text-gray-900">
              {member.name}
            </span>
            <span className="truncate text-[13px] text-gray-500">
              {member.detail}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={expanded}
            aria-label={expanded ? "Hide coaching tools" : "Show coaching tools"}
            className="grid h-8 w-8 place-items-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${
                expanded ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {expanded && (
        // Indent to align under the name (36px tile + 16px gap).
        <div className="pb-4 pl-[52px]">
          <CoachingActions
            userId={member.userId}
            userName={member.name}
            workspaceId={workspaceId}
            timeframeDays={timeframeDays}
            tag={member.tag}
          />
          {href && (
            <Link
              href={href}
              className="group mt-3 inline-flex items-center gap-0.5 text-[12.5px] font-medium text-gray-600 transition-colors hover:text-brand-700"
            >
              View full profile
              <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function AttentionList({ attention, workspaceId, timeframeDays }) {
  if (!attention?.length) return null;
  return (
    <div className="animate-rise mb-8" style={{ "--rise-delay": "120ms" }}>
      <RuleHeader
        title="Needs attention"
        count={attention.length}
        actionLabel="Team report"
        actionHref="/workspaces/reports/manager-dashboard"
      />
      <div>
        {attention.map((member) => (
          <AttentionRow
            key={member.userId || member.name}
            member={member}
            workspaceId={workspaceId}
            timeframeDays={timeframeDays}
          />
        ))}
      </div>
    </div>
  );
}

function RolloutList({ rollouts }) {
  if (!rollouts?.length) return null;
  return (
    <div className="animate-rise mb-8" style={{ "--rise-delay": "200ms" }}>
      <RuleHeader
        title="Learning path rollout"
        actionLabel="All paths"
        actionHref="/dashboard/learning-paths"
      />
      <div>
        {rollouts.map((rollout) => {
          const rowClass =
            "flex items-center gap-4 border-b border-gray-200/70 py-3.5 last:border-b-0";
          const inner = (
            <>
              <span className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100">
                {rollout.imageURL ? (
                  <Image
                    src={rollout.imageURL}
                    alt=""
                    fill
                    className="object-cover"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-brand-50 text-brand-600">
                    <GraduationCap className="h-4 w-4" strokeWidth={1.8} />
                  </span>
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[15px] font-semibold text-gray-900">
                  {rollout.title}
                </span>
                <span className="truncate text-[13px] text-gray-500">
                  {rollout.done} done · {rollout.inProgress} in progress ·{" "}
                  {rollout.notStarted} not started
                  {rollout.dueLabel ? ` · due ${rollout.dueLabel}` : ""}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2.5">
                <ProgressBar
                  value={rollout.donePct}
                  secondary={rollout.inProgressPct}
                  className="hidden w-[80px] sm:flex"
                />
                <span className="w-9 text-right text-[13px] font-semibold tabular-nums text-gray-900">
                  {rollout.donePct}%
                </span>
              </span>
            </>
          );
          const href = adminLearningPathHref(rollout.id);
          return href ? (
            <Link
              key={rollout.id}
              href={href}
              className={`${rowClass} transition-colors hover:bg-black/1.5`}
            >
              {inner}
            </Link>
          ) : (
            <div key={rollout.title} className={rowClass}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminDashboard({ initialData, initialPeriod = 30 }) {
  const { data: session } = useSession();
  const workspaceId = session?.user?.workspaceId;
  const [period, setPeriod] = useState(initialPeriod);
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  const changePeriod = useCallback(async (days) => {
    setLoading(true);
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    try {
      const next = await getHomeAdminData(days);
      // Only commit the new period once its data has loaded, so a failed fetch
      // never leaves the selector showing a window the data doesn't reflect.
      // Ignore stale responses if the period changed again mid-flight.
      if (requestRef.current === requestId && next) {
        setData(next);
        setPeriod(days);
      }
    } catch (error) {
      console.error("[Home] Failed to load team data for period", error);
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, []);

  return (
    <>
      <div
        className="animate-rise mb-3 flex items-center justify-between gap-3"
        style={{ "--rise-delay": "20ms" }}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
          Team overview
        </span>
        <PeriodSelect value={period} onChange={changePeriod} disabled={loading} />
      </div>

      <div
        className={`transition-opacity ${loading ? "pointer-events-none opacity-60" : ""}`}
        aria-busy={loading}
      >
        <StatLine stats={data?.stats} />
        <AttentionList
          attention={data?.attention}
          workspaceId={workspaceId}
          timeframeDays={period}
        />
        <RolloutList rollouts={data?.rollouts} />
      </div>
    </>
  );
}
